import { FC, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import type { Feature, FeatureCollection, Geometry, Point } from "geojson";
import i18n from "src/i18n/config";
import { useLanguage } from "src/hooks/useLanguage";
import { toArabicIndicDigitsInString } from "src/utils/numberFormat";
import maplibregl, { GeoJSONSource } from "maplibre-gl";
import {
  applyEnglishPrimaryMapLabels,
  FOLLOWERS_MAP_VECTOR_STYLE_URL,
} from "src/dashboards/organicMedia/components/maps/followersMapBaseStyle";
import {
  FOLLOWERS_MAP_PIN_CLICK_ZOOM,
  FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID,
  FOLLOWERS_MAP_PIN_IMAGE_ID,
  ensureStandardMapPinOnMap,
  mapPinWeightedIconSize,
  MAP_PIN_BASE_ICON_OPACITY,
  MAP_PIN_FOCUS_ICON_OPACITY,
} from "src/dashboards/organicMedia/components/maps/followersMapPin";
import "src/dashboards/organicMedia/components/maps/FollowersMapLibre.scss";

// MapLibre's NavigationControl bakes its button titles into the library in
// English with no i18n hook of its own; the `locale` map on the `Map`
// constructor is MapLibre's own mechanism for overriding those built-in
// strings (see `NavigationControl.ZoomIn`/`ZoomOut` in maplibre-gl's source).
const NAVIGATION_CONTROL_LOCALE_AR: Record<string, string> = {
  "NavigationControl.ZoomIn": "تكبير",
  "NavigationControl.ZoomOut": "تصغير",
};

const zoomButtonTitles = (isArabic: boolean) => ({
  in: isArabic ? NAVIGATION_CONTROL_LOCALE_AR["NavigationControl.ZoomIn"] : "Zoom in",
  out: isArabic ? NAVIGATION_CONTROL_LOCALE_AR["NavigationControl.ZoomOut"] : "Zoom out",
});

/** Updates the already-rendered Zoom in/out button titles in place — used on
 * a language switch, since MapLibre only reads `locale` once, at construction. */
const applyZoomControlLocale = (map: maplibregl.Map, isArabic: boolean) => {
  const titles = zoomButtonTitles(isArabic);
  const container = map.getContainer();
  const zoomInButton = container.querySelector<HTMLButtonElement>(".maplibregl-ctrl-zoom-in");
  const zoomOutButton = container.querySelector<HTMLButtonElement>(".maplibregl-ctrl-zoom-out");
  if (zoomInButton) {
    zoomInButton.title = titles.in;
    zoomInButton.setAttribute("aria-label", titles.in);
  }
  if (zoomOutButton) {
    zoomOutButton.title = titles.out;
    zoomOutButton.setAttribute("aria-label", titles.out);
  }
};

const SOURCE_ID = "shopify-geo-cities";
const SYMBOL_LAYER_BASE_ID = "shopify-geo-cities-pin-base";
const SYMBOL_LAYER_FOCUS_ID = "shopify-geo-cities-pin-focus";
const SYMBOL_QUERY_LAYERS = [SYMBOL_LAYER_FOCUS_ID, SYMBOL_LAYER_BASE_ID];

type CityProps = {
  name: string;
  revenue: number;
  revenueFormattedValue?: string;
  id: string;
  customers?: number;
  customersFormattedValue?: string;
  orders?: number;
  ordersFormattedValue?: string;
  return_count?: number;
  return_countFormattedValue?: string;
};

export interface ShopifyCityMapRow {
  city: string;
  net_revenue: number;
  /** Pre-formatted amount from the API (e.g. "USD 2,088"), preferred over formatting `net_revenue` locally. */
  net_revenueFormattedValue?: string;
  customers?: number;
  customersFormattedValue?: string;
  customer_count?: number;
  orders?: number;
  ordersFormattedValue?: string;
  order_count?: number;
  return_value?: number;
  return_valueFormattedValue?: string;
  return_count?: number;
  return_countFormattedValue?: string;
  latitude?: number;
  longitude?: number;
}

const PAK_CITY_COORDS: Record<string, [number, number]> = {
  lahore: [74.3587, 31.5204],
  faisalabad: [73.0845, 31.4504],
  rawalpindi: [73.0479, 33.6844],
  multan: [71.4782, 30.1575],
  gujranwala: [74.1871, 32.1617],
  sialkot: [74.5229, 32.4945],
  sargodha: [72.6711, 32.0836],
  bahawalpur: [71.6752, 29.3956],
  sheikhupura: [73.9876, 31.7167],
  gujrat: [74.0789, 32.5731],
  karachi: [67.0011, 24.8607],
  hyderabad: [68.3737, 25.3960],
  sukkur: [68.8574, 27.7052],
  larkana: [68.2141, 27.5607],
  nawabshah: [68.4126, 26.2483],
  mirpurkhas: [69.0126, 25.5267],
  jacobabad: [68.4382, 28.2810],
  quetta: [66.9750, 30.1798],
  gwadar: [62.3250, 25.1264],
  khuzdar: [66.6430, 27.8128],
  turbat: [63.0440, 26.0012],
  chaman: [66.4511, 30.9175],
  zhob: [69.4493, 31.3408],
  peshawar: [71.5249, 34.0151],
  mardan: [72.0478, 34.1980],
  abbottabad: [73.2215, 34.1688],
  swat: [72.3609, 34.7717],
  swabi: [72.4647, 34.1239],
  kohat: [71.4415, 33.5889],
  bannu: [70.6041, 32.9889],
  muzaffarabad: [73.4708, 34.3700],
  mirpur: [73.7517, 33.1488],
  gilgit: [74.3080, 35.9208],
  skardu: [75.6333, 35.2903],
};

const REGION_VIEW: Record<string, { center: [number, number]; zoom: number }> = {
  punjab: { center: [73.5, 31.2], zoom: 5.8 },
  sindh: { center: [68.2, 25.6], zoom: 5.8 },
  balochistan: { center: [65.6, 28.3], zoom: 5.3 },
  kpk: { center: [71.7, 34.2], zoom: 5.9 },
  ajk: { center: [74.0, 34.3], zoom: 6.2 },
};

// Unicode-aware so localised (e.g. Arabic) city names survive normalisation instead of
// collapsing to an empty string — see the matching helper in ShopifyGeoSpatialDashboard.
const normalizeCity = (name: string) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

function toFallbackCoordinates(city: string): [number, number] | null {
  const normalized = normalizeCity(city);
  if (PAK_CITY_COORDS[normalized]) {
    return PAK_CITY_COORDS[normalized];
  }
  const match = Object.entries(PAK_CITY_COORDS).find(
    ([key]) => normalized.includes(key) || key.includes(normalized),
  );
  return match?.[1] ?? null;
}

export function coordsFromRow(row: ShopifyCityMapRow): [number, number] | null {
  const { latitude: lat, longitude: lng } = row;
  if (typeof lat === "number" && typeof lng === "number") {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }
    return [lng, lat];
  }
  return toFallbackCoordinates(row.city);
}

export function countMappableShopifyCities(cities: ShopifyCityMapRow[]): number {
  return cities.filter(c => coordsFromRow(c) !== null).length;
}


function getMapTooltipPosition(x: number, y: number, isRTL: boolean) {
  const tooltipWidth = 320;
  const tooltipHeight = 132;
  const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 768 : window.innerHeight;
  const preferredLeft = isRTL ? x - 12 - tooltipWidth : x + 12;
  const left = Math.max(8, Math.min(preferredLeft, viewportWidth - tooltipWidth - 8));
  const top = Math.max(8, Math.min(y + 18, viewportHeight - tooltipHeight - 8));

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${tooltipWidth}px`,
    maxWidth: "calc(100vw - 16px)",
    boxSizing: "border-box" as const,
  };
}

interface ShopifyCityMapLibreProps {
  cities: ShopifyCityMapRow[];
  shapeKey: "punjab" | "sindh" | "balochistan" | "kpk" | "ajk";
  regionId: string;
  regionTitle: string;
  metricLabel: string;
  /** PKR amount line in tooltip (already localized). */
  formatRevenue: (_amount: number) => string;
  onCityPinClick?: (_city: string) => void;
  /** Highlight pin when city cross-filter matches this region + city. */
  selectedCityNormalized?: string | null;
  selectedRegionId?: string | null;
  /** When set, map section aria-label includes the active city (same region as filter). */
  crossFilterCityLabel?: string | null;
}

const ShopifyCityMapLibre: FC<ShopifyCityMapLibreProps> = ({
  cities,
  shapeKey,
  regionId,
  regionTitle,
  metricLabel,
  formatRevenue,
  onCityPinClick,
  selectedCityNormalized,
  selectedRegionId,
  crossFilterCityLabel,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  /** Hover-popup counts have no API `*FormattedValue` fallback, so convert their digits here. */
  const localizeCount = (value: number) =>
    isRTL
      ? toArabicIndicDigitsInString(Math.round(value).toLocaleString())
      : Math.round(value).toLocaleString();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const geoJsonRef = useRef<FeatureCollection<Geometry, CityProps>>({
    type: "FeatureCollection",
    features: [],
  });
  const maxRevenueRef = useRef(1);
  const hoveredPointIdRef = useRef<string | number | null>(null);

  const [tooltip, setTooltip] = useState<{
    name: string;
    value: number;
    formattedValue?: string;
    customers?: number;
    customersFormattedValue?: string;
    orders?: number;
    ordersFormattedValue?: string;
    return_count?: number;
    return_countFormattedValue?: string;
    x: number;
    y: number;
  } | null>(null);

  const view = REGION_VIEW[shapeKey] ?? REGION_VIEW.punjab;

  const { features, maxRevenue } = useMemo(() => {
    const list: Feature<Geometry, CityProps>[] = [];
    let max = 1;
    cities.forEach((row, index) => {
      const coords = coordsFromRow(row);
      if (!coords) {
        return;
      }
      max = Math.max(max, row.net_revenue);
      const id = `${regionId}-${index}-${row.city}`;
      list.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: coords } satisfies Point,
        properties: {
          name: row.city,
          revenue: row.net_revenue,
          revenueFormattedValue: row.net_revenueFormattedValue,
          customers: row.customers ?? row.customer_count,
          customersFormattedValue: row.customersFormattedValue,
          orders: row.orders ?? row.order_count,
          ordersFormattedValue: row.ordersFormattedValue,
          return_count: row.return_count,
          return_countFormattedValue: row.return_countFormattedValue,
          id,
        },
      });
    });
    return { features: list, maxRevenue: max };
  }, [cities, regionId]);

  const featuresRef = useRef(features);
  featuresRef.current = features;

  maxRevenueRef.current = maxRevenue;

  const onCityPinClickRef: MutableRefObject<typeof onCityPinClick> = useRef(onCityPinClick);
  onCityPinClickRef.current = onCityPinClick;

  const clearPointHover = (map: maplibregl.Map) => {
    const prev = hoveredPointIdRef.current;
    if (prev !== null && prev !== undefined) {
      map.removeFeatureState({ source: SOURCE_ID, id: prev }, "hover");
    }
    hoveredPointIdRef.current = null;
  };

  const applySelectionState = (map: maplibregl.Map) => {
    const fc = geoJsonRef.current;
    const wantCity = selectedCityNormalized?.trim().toLowerCase() ?? "";
    const wantRegion = selectedRegionId?.trim().toLowerCase() ?? "";
    const cityActive =
      wantCity &&
      (!wantRegion || wantRegion === regionId.trim().toLowerCase());
    for (const f of fc.features) {
      const fid = (f.properties as CityProps).id;
      const name = String((f.properties as CityProps).name ?? "")
        .trim()
        .toLowerCase();
      map.setFeatureState(
        { source: SOURCE_ID, id: fid },
        {
          selected: Boolean(cityActive && name === wantCity),
        },
      );
    }
  };

  const applyCityDataToMap = (map: maplibregl.Map, fit: boolean) => {
    const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) {
      return;
    }
    const fc = geoJsonRef.current;
    src.setData(fc);
    applySelectionState(map);

    if (!fit || fc.features.length === 0) {
      return;
    }

    const bounds = new maplibregl.LngLatBounds();
    for (const f of fc.features) {
      if (f.geometry.type === "Point") {
        bounds.extend(f.geometry.coordinates as [number, number]);
      }
    }
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 48, maxZoom: 9.5, duration: 550 });
    }
  };

  useEffect(() => {
    const bumpResize = () => {
      const map = mapRef.current;
      if (!map?.loaded()) {
        return;
      }
      requestAnimationFrame(() => {
        map.resize();
        applyZoomControlLocale(map, i18n.language?.toLowerCase().startsWith("ar") ?? false);
      });
    };
    i18n.on("languageChanged", bumpResize);
    return () => {
      i18n.off("languageChanged", bumpResize);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    let mapDestroyed = false;

    const isArabic = i18n.language?.toLowerCase().startsWith("ar") ?? false;
    const map = new maplibregl.Map({
      container: el,
      style: FOLLOWERS_MAP_VECTOR_STYLE_URL,
      center: view.center,
      zoom: view.zoom,
      attributionControl: false,
      locale: isArabic ? NAVIGATION_CONTROL_LOCALE_AR : undefined,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    map.on("load", () => {
      applyEnglishPrimaryMapLabels(map);
      void (async () => {
        const pinOk = await ensureStandardMapPinOnMap(map);
        if (!pinOk || mapDestroyed) {
          return;
        }

        if (!map.getSource(SOURCE_ID)) {
          geoJsonRef.current = {
            type: "FeatureCollection",
            features: featuresRef.current,
          };
          map.addSource(SOURCE_ID, {
            type: "geojson",
            data: geoJsonRef.current,
            promoteId: "id",
          });
        }

        const maxR = maxRevenueRef.current;

        if (!map.getLayer(SYMBOL_LAYER_BASE_ID)) {
          map.addLayer({
            id: SYMBOL_LAYER_BASE_ID,
            type: "symbol",
            source: SOURCE_ID,
            layout: {
              "icon-image": FOLLOWERS_MAP_PIN_IMAGE_ID,
              "icon-size": mapPinWeightedIconSize(maxR, "revenue"),
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "icon-anchor": "bottom",
            },
            paint: { "icon-opacity": MAP_PIN_BASE_ICON_OPACITY },
          });
        }

        if (!map.getLayer(SYMBOL_LAYER_FOCUS_ID)) {
          map.addLayer({
            id: SYMBOL_LAYER_FOCUS_ID,
            type: "symbol",
            source: SOURCE_ID,
            layout: {
              "icon-image": FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID,
              "icon-size": mapPinWeightedIconSize(maxR, "revenue"),
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "icon-anchor": "bottom",
            },
            paint: { "icon-opacity": MAP_PIN_FOCUS_ICON_OPACITY },
          });
        }

        applyCityDataToMap(map, geoJsonRef.current.features.length > 0);

        const onMove = (e: maplibregl.MapMouseEvent) => {
          const hit = map.queryRenderedFeatures(e.point, {
            layers: SYMBOL_QUERY_LAYERS,
          })[0] as maplibregl.MapGeoJSONFeature | undefined;

          if (!hit) {
            clearPointHover(map);
            setTooltip(null);
            map.getCanvas().style.cursor = "";
            return;
          }

          const fid = hit.id;
          if (fid === undefined || fid === null) {
            return;
          }

          if (hoveredPointIdRef.current !== fid) {
            clearPointHover(map);
            hoveredPointIdRef.current = fid;
            map.setFeatureState({ source: SOURCE_ID, id: fid }, { hover: true });
          }

          map.getCanvas().style.cursor = onCityPinClickRef.current ? "pointer" : "";

          const name = String(hit.properties?.name ?? "");
          const value = Number(hit.properties?.revenue ?? 0);
          const formattedValue = hit.properties?.revenueFormattedValue;
          const customers = Number(hit.properties?.customers);
          const customersFormattedValue = hit.properties?.customersFormattedValue;
          const orders = Number(hit.properties?.orders);
          const ordersFormattedValue = hit.properties?.ordersFormattedValue;
          const returnCount = Number(hit.properties?.return_count);
          const returnCountFormattedValue = hit.properties?.return_countFormattedValue;

          setTooltip({
            name,
            value,
            formattedValue: typeof formattedValue === "string" && formattedValue ? formattedValue : undefined,
            customers: Number.isFinite(customers) ? customers : undefined,
            customersFormattedValue:
              typeof customersFormattedValue === "string" && customersFormattedValue ? customersFormattedValue : undefined,
            orders: Number.isFinite(orders) ? orders : undefined,
            ordersFormattedValue:
              typeof ordersFormattedValue === "string" && ordersFormattedValue ? ordersFormattedValue : undefined,
            return_count: Number.isFinite(returnCount) ? returnCount : undefined,
            return_countFormattedValue:
              typeof returnCountFormattedValue === "string" && returnCountFormattedValue ? returnCountFormattedValue : undefined,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
          });
        };

        const onLeave = () => {
          clearPointHover(map);
          setTooltip(null);
          map.getCanvas().style.cursor = "";
        };

        let lastShopifyPinClickMs = 0;
        const onPinClick = (e: maplibregl.MapMouseEvent) => {
          const now = performance.now();
          if (now - lastShopifyPinClickMs < 120) {
            return;
          }
          lastShopifyPinClickMs = now;
          const cb = onCityPinClickRef.current;
          if (!cb) {
            return;
          }
          const hit = map.queryRenderedFeatures(e.point, {
            layers: SYMBOL_QUERY_LAYERS,
          })[0] as maplibregl.MapGeoJSONFeature | undefined;
          if (!hit?.properties?.name) {
            return;
          }
          cb(String(hit.properties.name));
          if (!hit.geometry || hit.geometry.type !== "Point") {
            return;
          }
          const coords = hit.geometry.coordinates as [number, number];
          map.easeTo({
            center: coords,
            zoom: Math.max(map.getZoom(), FOLLOWERS_MAP_PIN_CLICK_ZOOM),
            duration: 650,
          });
        };

        for (const lid of SYMBOL_QUERY_LAYERS) {
          map.on("mousemove", lid, onMove);
          map.on("mouseleave", lid, onLeave);
          map.on("click", lid, onPinClick);
        }
      })();
    });

    return () => {
      mapDestroyed = true;
      map.remove();
      mapRef.current = null;
      hoveredPointIdRef.current = null;
    };
  }, [shapeKey, view.center, view.zoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) {
      return;
    }
    geoJsonRef.current = { type: "FeatureCollection", features };
    applyCityDataToMap(map, features.length > 0);
    for (const lid of SYMBOL_QUERY_LAYERS) {
      if (map.getLayer(lid)) {
        map.setLayoutProperty(lid, "icon-size", mapPinWeightedIconSize(maxRevenue, "revenue"));
      }
    }
  }, [features, maxRevenue]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) {
      return;
    }
    applySelectionState(map);
  }, [selectedCityNormalized, selectedRegionId, regionId, features]);

  if (features.length === 0) {
    return null;
  }

  const mapSectionAria = crossFilterCityLabel?.trim()
    ? t("shopifyGeo.cityMap.mapAriaWithCity", {
        region: regionTitle,
        city: crossFilterCityLabel.trim(),
      })
    : t("shopifyGeo.cityMap.mapAria", { region: regionTitle });

  return (
    <section
      className="followers-maplibre followers-maplibre--shopify-geo"
      aria-label={mapSectionAria}
      onMouseLeave={() => {
        const map = mapRef.current;
        if (map) {
          clearPointHover(map);
          map.getCanvas().style.cursor = "";
        }
        setTooltip(null);
      }}
    >
      <div ref={containerRef} className="followers-maplibre__canvas" role="presentation" />
      {tooltip && (
        <div
          className="followers-maplibre__tooltip shopify-geo-map-tooltip"
          style={getMapTooltipPosition(tooltip.x, tooltip.y, isRTL)}
        >
          <span className="shopify-geo-map-tooltip__title">{tooltip.name}</span>
          <span className="shopify-geo-map-tooltip__row">
            <span className="shopify-geo-map-tooltip__label">{metricLabel}</span>
            <strong className="shopify-geo-map-tooltip__value">
              {tooltip.formattedValue ?? formatRevenue(tooltip.value)}
            </strong>
          </span>
          <span className="shopify-geo-map-tooltip__row">
            <span className="shopify-geo-map-tooltip__label">
              {t("shopifyGeo.mapTooltip.orders")}
            </span>
            <strong className="shopify-geo-map-tooltip__value">
              {tooltip.ordersFormattedValue ?? localizeCount(tooltip.orders ?? 0)}
            </strong>
          </span>
          <span className="shopify-geo-map-tooltip__row">
            <span className="shopify-geo-map-tooltip__label">
              {t("shopifyGeo.mapTooltip.customers")}
            </span>
            <strong className="shopify-geo-map-tooltip__value">
              {tooltip.customersFormattedValue ?? localizeCount(tooltip.customers ?? 0)}
            </strong>
          </span>
          {tooltip.return_count !== undefined && (
            <span className="shopify-geo-map-tooltip__row">
              <span className="shopify-geo-map-tooltip__label">
                {t("shopifyGeo.mapTooltip.returnOrders")}
              </span>
              <strong className="shopify-geo-map-tooltip__value">
                {tooltip.return_countFormattedValue ?? localizeCount(tooltip.return_count)}
              </strong>
            </span>
          )}
        </div>
      )}
    </section>
  );
};

export default ShopifyCityMapLibre;
